import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    // Only run on initial mount
    if (isInitialMount) {
      setIsInitialMount(false);
      
      // Load user from localStorage on mount
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');
      
      if (savedUser && savedToken) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setLoading(false);
          return;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      // Fetch current user if token exists but no user data
      if (savedToken && !savedUser) {
        fetchCurrentUser();
      } else {
        setLoading(false);
      }
    }
  }, []); // Only run on mount

  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, mfaCode) => {
    try {
      const response = await authAPI.login(username, password, mfaCode);

      // Staff-MFA gate (require_2fa): server returns this with no token when a
      // TOTP code is needed. Not an error — the form should prompt for it.
      if (response.data?.mfa_required) {
        setLoading(false);
        return { success: false, mfaRequired: true };
      }

      const { token: newToken, user: userData } = response.data;

      // Save to localStorage first (synchronous)
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set state - React will batch these but they'll both update
      setToken(newToken);
      setUser(userData);
      setLoading(false); // Ensure loading is false after login
      
      // Force a re-render by ensuring state is set
      // Return success with user data
      return { success: true, user: userData };
    } catch (error) {
      setLoading(false);
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid credentials',
      };
    }
  };

  const logout = () => {
    // Phase 3.3: revoke the token server-side (best-effort), then clear locally.
    authAPI.logout().catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Stand-in covers running today (Growth Access → Stand-ins): [{role, role_label, starts_on, ends_on}].
  // The person keeps their own access and also gets the covered role's (users/covers.py).
  // coversLoaded keeps route guards from turning a stand-in away before their covers arrive.
  const [covers, setCovers] = useState([]);
  const [coversLoaded, setCoversLoaded] = useState(false);
  useEffect(() => {
    if (!user || !token) { setCovers([]); setCoversLoaded(true); return; }
    setCoversLoaded(false);
    import('../services/api').then(({ roleCoversAPI }) => {
      roleCoversAPI.mine()
        .then(res => setCovers(Array.isArray(res.data) ? res.data : []))
        .catch(() => setCovers([]))
        .finally(() => setCoversLoaded(true));
    });
  }, [user, token]);
  const isCovering = (role) => covers.some((c) => c.role === role);

  const isChairman = () => user?.role?.toUpperCase() === 'CHAIRMAN';
  // Default full finance visibility — everyone else needs an explicit, revocable FinanceAccessGrant.
  const isFinanceLeadership = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'CFO';
  };
  const isAdmin = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'ADMIN' || role === 'CEO' || role === 'COO' || user?.is_superuser === true;
  };
  const showCeoStrategicMetrics = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'ADMIN' || role === 'CEO';
  };
  const isCeoOrCoo = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'CEO' || role === 'COO';
  };
  // Only Chairman/Admin may suspend or reinstate an agency/country — not CEO/COO
  const canSuspendAgencies = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'ADMIN';
  };
  const isAgent = () => user?.role?.toUpperCase() === 'AGENT';
  const isEmployee = () => user?.role?.toUpperCase() === 'PERMANENT_EMPLOYEE';
  const isSupport = () => user?.role?.toUpperCase() === 'SUPPORT';
  const isAgentOrEmployee = () => isAgent() || isEmployee();
  const isCsm = () => user?.role?.toUpperCase() === 'CSM';
  // Full sales-pipeline visibility — students, alumni, agent reassignment, commissions. Least-privilege: not folded into isAdmin.
  const isSalesLeadership = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'CSM' || isCovering('CSM');
  };
  const isCmo = () => user?.role?.toUpperCase() === 'CMO';
  // Full mobile_content/marketing control. Least-privilege: not folded into isAdmin.
  const isMarketingLeadership = () => {
    const role = user?.role?.toUpperCase();
    return role === 'CHAIRMAN' || role === 'CMO';
  };
  // Any non-student staff member — used for adminOrEmployeeOnly route guards. Includes CSM (sales
  // leadership needs the general staff Dashboard) but not CMO (marketing has no general-staff need).
  const isAnyStaff = () => {
    const role = user?.role?.toUpperCase();
    return ['CHAIRMAN', 'ADMIN', 'CEO', 'COO', 'CSM', 'PERMANENT_EMPLOYEE', 'AGENT', 'SUPPORT'].includes(role);
  };
  // isAnyStaff minus AGENT — mirrors User.is_internal_staff. Agents add their own
  // clients but don't see inbound leads, the support inbox, deleted records or
  // KVKK requests, and don't sign billing documents.
  const isInternalStaff = () => isAnyStaff() && !isAgent();
  // Internal staff + the CMO (founder, 22 Oct 2026): leads, Action Board, meetings, team tasks.
  // Mirrors User.is_core_team; client screens stay internal-staff only.
  const isCoreTeam = () => isInternalStaff() || isCmo();
  // Who reads the marketing plan (marketing/services.can_view): CMO, Chairman, CEO, COO, CSM, Admin.
  const canViewMarketing = () => {
    const role = user?.role?.toUpperCase();
    return ['CHAIRMAN', 'CMO', 'CEO', 'COO', 'CSM', 'ADMIN'].includes(role) || user?.is_superuser === true
      || isCovering('CMO');
  };
  // Role duty routines: the core team plus the CFO.
  const hasDuties = () => isCoreTeam() || user?.role?.toUpperCase() === 'CFO';
  // Coach (per-role analytics Q7a): reads everyone's My Analytics and the board report, nothing else.
  const isCoach = () => user?.role?.toUpperCase() === 'COACH';
  // My Analytics: every internal team login (foundation TEAM_ROLES) plus the Coach. Mirrors my_analytics.can_open_page.
  const canOpenMyAnalytics = () => {
    const role = user?.role?.toUpperCase();
    return ['CHAIRMAN', 'CEO', 'COO', 'CFO', 'CMO', 'CSM', 'ADMIN', 'PERMANENT_EMPLOYEE', 'SUPPORT', 'COACH'].includes(role)
      || user?.is_superuser === true;
  };

  // Home (brainstorms/2026-10-27-dashboard-for-all-users.md): the nine internal team roles land on
  // it; agents use the partner portal, the Coach My Analytics. Mirrors students/home.py HOME_ROLES.
  const HOME_ROLES = ['CHAIRMAN', 'CFO', 'ADMIN', 'CEO', 'COO', 'CSM', 'CMO', 'PERMANENT_EMPLOYEE', 'SUPPORT'];
  const canOpenHome = () => HOME_ROLES.includes(user?.role?.toUpperCase()) || user?.is_superuser === true;

  // Billing documents (founder rule, 19 Oct 2026): COO/CEO/CSM/Chairman/CFO (+ Admin, a superuser)
  // issue and send them, every internal staff member drafts; only the CFO or Chairman role records a
  // payment (Chairman added 20 Oct 2026) — not a superuser. Mirrors deals/billing.py.
  const isCfo = () => user?.role?.toUpperCase() === 'CFO';
  const canIssueBillingDocs = () => {
    const role = user?.role?.toUpperCase();
    return ['CHAIRMAN', 'CFO', 'ADMIN', 'CEO', 'COO', 'CSM'].includes(role) || user?.is_superuser === true;
  };
  const canRecordPayments = () => isCfo() || isChairman();
  // Payment Status on a client record (students/payment_lock.py): the payment recorders plus the
  // CEO (founder, 28 Oct 2026). The CEO still cannot record payments.
  const canSetPaymentStatus = () => canRecordPayments() || ['CEO', 'COO'].includes(user?.role?.toUpperCase());

  // Mobile-content admin: CEO/COO/Chairman (role) or CMO (role) or a delegated user. isMobileContentAdmin
  // keeps isCeoOrCoo's own literal meaning intact for the places that specifically mean CEO-or-COO
  // (e.g. StudentAppAdmin's delegate-exclusion filter), while everywhere access itself is checked
  // should use this combined hook instead.
  const isMobileContentAdmin = () => isCeoOrCoo() || isCmo();

  const [hasMobileAdminDelegation, setHasMobileAdminDelegation] = useState(false);

  useEffect(() => {
    if (user && token && !isMobileContentAdmin()) {
      // Check delegation status
      import('../services/api').then(({ mobileContentAPI }) => {
        mobileContentAPI.delegations.myAccess()
          .then(res => setHasMobileAdminDelegation(res.data?.has_access === true))
          .catch(() => setHasMobileAdminDelegation(false));
      });
    } else if (isMobileContentAdmin()) {
      setHasMobileAdminDelegation(true);
    } else {
      setHasMobileAdminDelegation(false);
    }
  }, [user, token]);

  const hasMobileAdminAccess = () => isMobileContentAdmin() || hasMobileAdminDelegation;

  // Finance nav visibility: role-based leadership always has access; anyone else needs to be
  // checked against their active FinanceAccessGrant status, fetched the same way mobile-content
  // delegation is above — otherwise a granted non-leadership user has API access but never sees
  // the nav link to discover it.
  const [hasActiveFinanceGrant, setHasActiveFinanceGrant] = useState(false);

  useEffect(() => {
    if (user && token && !isFinanceLeadership()) {
      import('../services/api').then(({ financeAPI }) => {
        financeAPI.myAccess()
          .then(res => setHasActiveFinanceGrant(res.data?.has_access === true))
          .catch(() => setHasActiveFinanceGrant(false));
      });
    } else {
      setHasActiveFinanceGrant(false);
    }
  }, [user, token]);

  const hasFinanceAccess = () => isFinanceLeadership() || hasActiveFinanceGrant;

  // Growth-stack access (Agencies + Sub-Agent Applications): exec board, or a
  // CEO/COO-granted delegation for a Permanent Employee (Phase 4.4).
  const [hasGrowthDelegation, setHasGrowthDelegation] = useState(false);
  useEffect(() => {
    if (user && token && !isAdmin()) {
      import('../services/api').then(({ growthDelegationsAPI }) => {
        growthDelegationsAPI.myAccess()
          .then(res => setHasGrowthDelegation(res.data?.has_access === true))
          .catch(() => setHasGrowthDelegation(false));
      });
    } else {
      setHasGrowthDelegation(isAdmin());
    }
  }, [user, token]);
  const hasGrowthStackAccess = () => isAdmin() || hasGrowthDelegation;

  // Memoize the value object to ensure isAuthenticated is computed correctly
  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    logout,
    isChairman,
    isFinanceLeadership,
    isAdmin,
    showCeoStrategicMetrics,
    isCeoOrCoo,
    canSuspendAgencies,
    isAgent,
    isEmployee,
    isSupport,
    isAgentOrEmployee,
    isCsm,
    isSalesLeadership,
    isCmo,
    isMarketingLeadership,
    isMobileContentAdmin,
    isAnyStaff,
    isInternalStaff,
    isCoreTeam,
    canViewMarketing,
    hasDuties,
    isCoach,
    canOpenMyAnalytics,
    canOpenHome,
    isCfo,
    canIssueBillingDocs,
    canRecordPayments,
    canSetPaymentStatus,
    hasMobileAdminAccess,
    hasFinanceAccess,
    hasGrowthStackAccess,
    covers,
    coversLoaded,
    isCovering,
    isAuthenticated: !!token && !!user,
  }), [
    user, token, loading, isChairman, isFinanceLeadership, isAdmin, showCeoStrategicMetrics, isCeoOrCoo,
    canSuspendAgencies, isAgent, isEmployee, isSupport, isAgentOrEmployee, isCsm, isSalesLeadership,
    isCmo, isMarketingLeadership, isMobileContentAdmin, isAnyStaff, isInternalStaff, isCoreTeam,
    canViewMarketing, hasDuties, isCfo, canOpenHome,
    canIssueBillingDocs, canRecordPayments, canSetPaymentStatus, hasMobileAdminAccess,
    hasMobileAdminDelegation, hasFinanceAccess, hasActiveFinanceGrant,
    hasGrowthStackAccess, hasGrowthDelegation, covers, coversLoaded,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

